import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { isAppLocale, SUPPORTED_LOCALES } from '@/i18n/config';
import { useLocale } from '@/i18n/useLocale';

/**
 * Each supported language is shown in its OWN name (autonym) via `Intl.DisplayNames`, so a reader
 * can find their language regardless of the current UI language and no per-language i18n string is
 * needed as locales are added.
 */
function autonym(locale: string): string {
  const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
}

/**
 * Header language control (SL-0027 → UI refinement). A compact dropdown — a globe plus the active
 * locale code (`IT`/`EN`) — that opens the full list of languages. This both makes the CURRENT
 * language explicit (shown in the trigger and check-marked in the menu) and scales to any number of
 * locales without widening the header, unlike the previous always-inline one-button-per-locale row.
 *
 * In the header it is a quiet 36px control, as the Brand Kit draws the header's tools: no outline
 * until hovered. `touch` is the drawer's version — 44px, outlined, and naming the language in full,
 * because there it stands in a list of preferences rather than beside other icons.
 */
const TRIGGER_CLASS = {
  default:
    'text-muted-foreground hover:bg-accent hover:text-foreground gap-1.5 border-transparent bg-transparent px-2 shadow-none data-[size=sm]:h-9 dark:bg-transparent dark:hover:bg-accent',
  touch: 'gap-2 data-[size=default]:h-11 min-w-32',
} as const;

export function LanguageSwitcher({ size = 'default' }: { size?: keyof typeof TRIGGER_CLASS } = {}) {
  const { t } = useTranslation('profile');
  const { locale, setLocale } = useLocale();

  return (
    <Select
      value={locale}
      onValueChange={(next) => {
        if (isAppLocale(next)) {
          setLocale(next);
        }
      }}
    >
      <SelectTrigger
        size={size === 'touch' ? 'default' : 'sm'}
        aria-label={t('language.title')}
        className={TRIGGER_CLASS[size]}
      >
        <Globe aria-hidden="true" className="size-4" />
        <span className="text-sm font-semibold">
          {size === 'touch' ? autonym(locale) : locale.toLocaleUpperCase(locale)}
        </span>
      </SelectTrigger>
      {/* `popper` (not the Select default `item-aligned`) so this header-edge dropdown uses collision
          detection and never opens off-screen. */}
      <SelectContent align="end" position="popper">
        {SUPPORTED_LOCALES.map((option) => (
          <SelectItem key={option} value={option}>
            {autonym(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
